import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from './Icon';
const CheckboxWrapper = styled.div `
  display: flex;
  gap: ${({ theme }) => theme.style.checkboxGap};
  font-size: ${({ theme }) => theme.style.checkboxFontSize};
  line-height: ${({ theme }) => theme.style.checkboxLineHeight};
  font-weight: ${({ theme }) => theme.style.checkboxFontWeight};
  white-space: nowrap;
  cursor: pointer;
  color: ${({ theme }) => theme.style.checkboxColor};
`;
const StyledCheckbox = styled.button `
  width: ${({ theme }) => theme.style.checkboxSize};
  height: ${({ theme }) => theme.style.checkboxSize};
  user-select: none;
  appearance: none;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  border-radius: ${({ theme }) => theme.style.checkboxBorderRadius};
  background-color: ${({ theme }) => theme.style.checkboxBackgroundColor};
  display: block;
  cursor: pointer;
  transition:
    box-shadow 0.2s,
    background-color 0.2s;
  position: relative;
  box-shadow:
    0 0 0 var(--checkbox-outline, 0px) var(--checkbox-outline-color, transparent),
    var(--checkbox-border-properties, ${({ theme }) => theme.style.checkboxBorderProperties})
      var(--checkbox-border-width, ${({ theme }) => theme.style.checkboxBorderWidth})
      var(--checkbox-border-color, ${({ theme }) => theme.style.checkboxBorderColor}),
    var(--checkbox-shadow, ${({ theme }) => theme.style.checkboxShadow});

  background-color: ${({ theme, $isChecked, $isIndeterminate }) => ($isChecked || $isIndeterminate) && theme.style.checkboxCheckedBackgroundColor};

  --checkbox-border-properties: ${({ theme, $isChecked, $isIndeterminate }) => ($isChecked || $isIndeterminate) && theme.style.checkboxCheckedBorderProperties};
  --checkbox-border-width: ${({ theme, $isChecked, $isIndeterminate }) => ($isChecked || $isIndeterminate) && theme.style.checkboxCheckedBorderWidth};
  --checkbox-border-color: ${({ theme, $isChecked, $isIndeterminate }) => ($isChecked || $isIndeterminate) && theme.style.checkboxCheckedBorderColor};
  --checkbox-shadow: ${({ theme, $isChecked, $isIndeterminate }) => ($isChecked || $isIndeterminate) && theme.style.checkboxCheckedShadow};

  --checkbox-border-color: ${({ $isHovered, theme }) => $isHovered && theme.style.checkboxHoveredBorderColor};
  background-color: ${({ $isHovered, theme }) => $isHovered && theme.style.checkboxHoveredBackgroundColor};

  background-color: ${({ theme, $isHovered, $isChecked, $isIndeterminate }) => $isHovered && ($isChecked || $isIndeterminate) && theme.style.checkboxCheckedHoveredBackgroundColor};
  --checkbox-border-color: ${({ theme, $isHovered, $isChecked, $isIndeterminate }) => $isHovered && ($isChecked || $isIndeterminate) && theme.style.checkboxCheckedHoveredBorderColor};

  --checkbox-border-color: ${({ $isFocussed, theme }) => $isFocussed && theme.style.checkboxFocussedBorderColor};
  --checkbox-outline: ${({ $isFocussed, theme }) => $isFocussed && theme.style.checkboxFocussedOutline};
  --checkbox-outline-color: ${({ theme, $isFocussed }) => $isFocussed && theme.style.checkboxFocussedOutlineColor};

  --checkbox-outline-color: ${({ theme, $isChecked, $isIndeterminate, $isFocussed }) => ($isChecked || $isIndeterminate) && $isFocussed && theme.style.checkboxCheckedFocussedOutlineColor};

  cursor: ${({ $isDisabled }) => $isDisabled && 'not-allowed'};
  opacity: ${({ $isDisabled }) => $isDisabled && 0.5};
`;
const IconCSS = css `
  --icon-size: ${({ theme }) => theme.style.checkboxSize};
  --icon-color: ${({ theme }) => theme.style.checkboxTickColor};
  position: absolute;
  top: 0;
  left: 0;
  filter: ${({ theme }) => theme.style.checkboxTickShadow};
  transition:
    stroke-dashoffset ${({ theme }) => theme.style.checkboxDuration} ${({ theme }) => theme.style.checkboxEasing},
    opacity 0.25s;
`;
const Checkmark = styled(Icon.Tick) `
  ${IconCSS}

  opacity: var(--checkbox-tick-opacity, 0);
  stroke-dasharray: 13px;
  stroke-dashoffset: var(--checkbox-offset, 13px);

  ${({ $isChecked }) => $isChecked &&
    css `
      --checkbox-offset: 0;
      --checkbox-tick-opacity: 1;
    `}
`;
const Indeterminate = styled(Icon.Indeterminate) `
  ${IconCSS}

  opacity: var(--checkbox-indeterminate-opacity, 0);
  stroke-dasharray: 8px;
  stroke-dashoffset: var(--checkbox-indeterminate-offset, 8px);

  ${({ $isIndeterminate }) => $isIndeterminate &&
    css `
      --checkbox-indeterminate-offset: 0;
      --checkbox-indeterminate-opacity: 1;
    `}
`;
export const Checkbox = (props) => {
    const [isIndeterminateState, setIsIndeterminateState] = useState(props.isIndeterminate ?? false);
    const [isHoveredState, setIsHoveredState] = useState(false);
    const [isFocussedState, setIsFocussedState] = useState(false);
    const [isPressedState, setIsPressedState] = useState(false);
    const styleProps = {
        $isChecked: props.isChecked ?? false,
        $isHovered: props.isHovered ?? isHoveredState,
        $isFocussed: props.isFocussed ?? isFocussedState,
        $isPressed: props.isPressed ?? isPressedState,
        $isDisabled: props.isDisabled ?? false,
        $isIndeterminate: isIndeterminateState,
    };
    const handleHoverChange = (value) => {
        if (props.onHoverChange && value !== isHoveredState) {
            props.onHoverChange(value);
        }
        setIsHoveredState(value);
    };
    const handleFocusChange = (value) => {
        if (props.onFocusChange && value !== isFocussedState) {
            props.onFocusChange(value);
        }
        setIsFocussedState(value);
    };
    const handlePressChange = (value) => {
        if (props.onPressChange && value !== isPressedState) {
            props.onPressChange(value);
        }
        if (value) {
            handleFocusChange(true);
        }
        setIsPressedState(value);
    };
    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const isChecked = !(props.isChecked ?? false);
        if (props.onCheckedChange && props.isChecked !== isChecked) {
            props.onCheckedChange(isChecked);
        }
        handleFocusChange(false);
        handlePressChange(false);
    };
    useEffect(() => {
        setIsIndeterminateState(false);
    }, [props.isChecked]);
    useEffect(() => {
        setIsIndeterminateState(props.isIndeterminate ?? false);
    }, [props.isIndeterminate]);
    return (React.createElement(CheckboxWrapper, { style: props.style, className: props.className, onClick: handleClick, onMouseEnter: () => handleHoverChange(true), onMouseMove: () => handleHoverChange(true), onMouseLeave: () => handleHoverChange(false), onMouseDown: (e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePressChange(true);
        }, onMouseUp: () => handlePressChange(false), ...styleProps },
        React.createElement(StyledCheckbox, { onFocus: () => handleFocusChange(true), onBlur: () => handleFocusChange(false), type: "button", ...styleProps },
            React.createElement(Checkmark, { ...styleProps }),
            React.createElement(Indeterminate, { ...styleProps })),
        props.label));
};
//# sourceMappingURL=Checkbox.js.map